import React from 'react';
import {
  Box,
  Heading,
  Text,
  Flex,
  Spinner,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Alert,
  AlertIcon,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Button,
  Select,
  Tooltip,
  Icon,
} from '@chakra-ui/react';
import { FiCalendar, FiCreditCard, FiCheckCircle, FiDownload, FiArrowRight } from 'react-icons/fi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import xhr from 'xhr';
import useAuth from 'hooks/useAuth';

dayjs.locale('es');

const CHARGE_TYPE_LABELS = {
  subject_activation: 'Activación materia',
};

const METHOD_LABELS = {
  transferencia: 'Transferencia bancaria',
  consignacion: 'Consignación',
  otro: 'Otro',
};

const TAB_KEYS = ['resumen', 'cargos', 'pagos', 'tarifas'];

function formatCOP(value) {
  return `$${Number(value || 0).toLocaleString('es-CO')}`;
}

function fullStudentName(student) {
  if (!student) return '—';
  return `${student.first_name || ''} ${student.last_name || ''}`.trim() || `Estudiante ${student.id}`;
}

export default function SchoolCobros() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const schoolName = user?.school?.name || '';

  const tabIndex = Math.max(0, TAB_KEYS.indexOf(searchParams.get('tab') || 'resumen'));

  const [loading, setLoading] = React.useState(true);
  const [overview, setOverview] = React.useState(null);
  const [charges, setCharges] = React.useState([]);
  const [payments, setPayments] = React.useState([]);
  const [tariffs, setTariffs] = React.useState([]);
  const [period, setPeriod] = React.useState('');

  const fetchAll = React.useCallback(async (currentPeriod) => {
    try {
      setLoading(true);
      const params = currentPeriod ? `?period=${currentPeriod}` : '';
      const [overviewRes, chargesRes, paymentsRes, tariffsRes] = await Promise.all([
        xhr('/school-usage-charges/me/overview'),
        xhr(`/school-usage-charges/me/list${params}`),
        xhr('/school-usage-charges/me/payments'),
        xhr('/school-usage-charges/me/tariffs'),
      ]);
      setOverview(overviewRes.data);
      setCharges(chargesRes.data?.data || []);
      setPayments(paymentsRes.data?.data || []);
      setTariffs(tariffsRes.data?.data || []);
    } catch (error) {
      console.error('Error fetching school cobros:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchAll(period);
  }, [fetchAll, period]);

  const setTab = (idx) => {
    const key = TAB_KEYS[idx] || 'resumen';
    setSearchParams(key === 'resumen' ? {} : { tab: key });
  };

  const pendingCharges = React.useMemo(
    () =>
      charges
        .filter((c) => c.status === 'pending')
        .sort((a, b) => dayjs(b.charge_date).valueOf() - dayjs(a.charge_date).valueOf()),
    [charges]
  );

  const periodOptions = React.useMemo(() => {
    const set = new Set();
    charges.forEach((c) => {
      if (c.charge_date) set.add(dayjs(c.charge_date).format('YYYY-MM'));
    });
    return Array.from(set)
      .sort((a, b) => b.localeCompare(a))
      .map((p) => ({ value: p, label: dayjs(`${p}-01`).format('MMMM YYYY') }));
  }, [charges]);

  const totalPendingFiltered = pendingCharges.reduce((s, c) => s + (c.amount || 0), 0);

  const exportCSV = () => {
    const rows = pendingCharges.map((c) => [
      fullStudentName(c.student),
      c.concept || '',
      c.program_name || '',
      CHARGE_TYPE_LABELS[c.charge_type] || c.charge_type,
      dayjs(c.charge_date).format('YYYY-MM-DD'),
      c.amount,
    ]);
    const header = ['Estudiante', 'Concepto', 'Programa', 'Tipo', 'Fecha', 'Monto'];
    const lines = [header, ...rows]
      .map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([`﻿${lines}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `cargos-pendientes-${dayjs().format('YYYY-MM-DD')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading && !overview) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="lg" />
      </Box>
    );
  }

  return (
    <Box>
      <Heading size="lg" mb={1}>
        Cobros
      </Heading>
      <Text color="gray.500" mb={4}>
        Uso de la plataforma de tu institución{schoolName ? ` · ${schoolName}` : ''}
      </Text>

      <Alert status="info" borderRadius="md" mb={6} bg="#fff5f5" color="gray.700">
        <AlertIcon as={FiCreditCard} color="#e53e3e" />
        <Text fontSize="sm">
          Los pagos se realizan por <b>transferencia bancaria</b> a Klazia. Una vez transferido, el equipo de Klazia registra el pago y tus cargos se marcan como liquidados aquí.
        </Text>
      </Alert>

      <Tabs index={tabIndex} onChange={setTab} colorScheme="purple">
        <TabList>
          <Tab>Resumen</Tab>
          <Tab>Cargos pendientes ({overview?.pendingCount ?? 0})</Tab>
          <Tab>Pagos ({payments.length})</Tab>
          <Tab>Tarifas vigentes</Tab>
        </TabList>

        <TabPanels>
          <TabPanel px={0}>
            <ResumenTab overview={overview} onGoToPagos={() => setTab(2)} />
          </TabPanel>

          <TabPanel px={0}>
            <Flex mb={4} alignItems="center" gap={3} flexWrap="wrap">
              <Select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                maxW="220px"
                bg="white"
                size="sm"
                icon={<FiCalendar />}
              >
                <option value="">Todos los periodos</option>
                {periodOptions.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </Select>
              <Button leftIcon={<FiDownload />} size="sm" variant="outline" onClick={exportCSV}>
                Exportar CSV
              </Button>
            </Flex>

            <TableContainer bg="white" borderRadius="lg" boxShadow="sm">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Estudiante</Th>
                    <Th>Concepto</Th>
                    <Th>Tipo</Th>
                    <Th>Fecha</Th>
                    <Th isNumeric>Monto</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {pendingCharges.map((c) => (
                    <Tr key={c.id}>
                      <Td maxW="180px">
                        <Tooltip label={fullStudentName(c.student)} openDelay={300}>
                          <Text noOfLines={1} fontWeight="medium">
                            {fullStudentName(c.student)}
                          </Text>
                        </Tooltip>
                      </Td>
                      <Td maxW="260px">
                        <Tooltip
                          label={[c.concept, c.program_name].filter(Boolean).join(' · ')}
                          openDelay={300}
                        >
                          <Box>
                            <Text noOfLines={1}>{c.concept}</Text>
                            {c.program_name && (
                              <Text noOfLines={1} fontSize="xs" color="gray.500">
                                {c.program_name}
                              </Text>
                            )}
                          </Box>
                        </Tooltip>
                      </Td>
                      <Td>
                        <Badge variant="subtle" colorScheme="gray" textTransform="uppercase">
                          {CHARGE_TYPE_LABELS[c.charge_type] || c.charge_type}
                        </Badge>
                      </Td>
                      <Td>{dayjs(c.charge_date).format('DD/MM/YY')}</Td>
                      <Td isNumeric fontWeight="semibold">
                        {formatCOP(c.amount)}
                      </Td>
                    </Tr>
                  ))}
                  {pendingCharges.length === 0 && (
                    <Tr>
                      <Td colSpan={5} textAlign="center" py={8} color="gray.500">
                        No hay cargos pendientes
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>

            <Flex justifyContent="flex-end" mt={4}>
              <Text fontWeight="bold">
                Total pendiente: {formatCOP(totalPendingFiltered)}
              </Text>
            </Flex>
          </TabPanel>

          <TabPanel px={0}>
            <TableContainer bg="white" borderRadius="lg" boxShadow="sm">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Fecha</Th>
                    <Th>Referencia</Th>
                    <Th>Método</Th>
                    <Th isNumeric>Monto</Th>
                    <Th>Cargos</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {payments.map((p) => (
                    <Tr key={p.id}>
                      <Td>{dayjs(p.payment_date).format('DD/MM/YY')}</Td>
                      <Td>{p.bank_reference}</Td>
                      <Td>{METHOD_LABELS[p.method] || p.method}</Td>
                      <Td isNumeric fontWeight="semibold" color="green.500">
                        {formatCOP(p.amount)}
                      </Td>
                      <Td>
                        <Badge colorScheme="green" variant="subtle">
                          {p.chargesCount} {p.chargesCount === 1 ? 'liquidado' : 'liquidados'}
                        </Badge>
                      </Td>
                    </Tr>
                  ))}
                  {payments.length === 0 && (
                    <Tr>
                      <Td colSpan={5} textAlign="center" py={8} color="gray.500">
                        No hay pagos registrados aún
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel px={0}>
            <Text color="gray.600" mb={4} fontSize="sm">
              Tarifas vigentes aplicadas a tu institución. Valores en COP por unidad de uso.
            </Text>
            <TableContainer bg="white" borderRadius="lg" boxShadow="sm">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Tipo de programa</Th>
                    <Th>Producto</Th>
                    <Th isNumeric>Tarifa</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {tariffs.map((t) => (
                    <Tr key={t.program_type_id}>
                      <Td>
                        <Flex alignItems="center" gap={2}>
                          <Text>{t.program_type_name}</Text>
                          {t.isOverride && (
                            <Tooltip label="Tarifa personalizada negociada con tu institución" openDelay={300}>
                              <Badge colorScheme="purple" variant="subtle">
                                Personalizada
                              </Badge>
                            </Tooltip>
                          )}
                        </Flex>
                      </Td>
                      <Td>Activación materia</Td>
                      <Td isNumeric fontWeight="semibold">
                        {formatCOP(t.price)}
                      </Td>
                    </Tr>
                  ))}
                  {tariffs.length === 0 && (
                    <Tr>
                      <Td colSpan={3} textAlign="center" py={8} color="gray.500">
                        No hay tarifas configuradas aún
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}

function ResumenTab({ overview, onGoToPagos }) {
  if (!overview) return null;

  return (
    <Box>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
        <Box bg="white" borderRadius="lg" boxShadow="sm" p={5}>
          <Flex alignItems="center" gap={3} mb={2}>
            <Flex
              w="40px"
              h="40px"
              borderRadius="md"
              bg="orange.50"
              color="orange.500"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={FiCalendar} />
            </Flex>
            <Stat>
              <StatLabel color="gray.500" fontSize="sm">
                Saldo pendiente
              </StatLabel>
              <StatNumber fontSize="2xl">{formatCOP(overview.pendingAmount)}</StatNumber>
            </Stat>
          </Flex>
        </Box>

        <Box bg="white" borderRadius="lg" boxShadow="sm" p={5}>
          <Flex alignItems="center" gap={3} mb={2}>
            <Flex
              w="40px"
              h="40px"
              borderRadius="md"
              bg="blue.50"
              color="blue.500"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={FiCreditCard} />
            </Flex>
            <Stat>
              <StatLabel color="gray.500" fontSize="sm">
                Cargos por liquidar
              </StatLabel>
              <StatNumber fontSize="2xl">{overview.pendingCount}</StatNumber>
            </Stat>
          </Flex>
        </Box>

        <Box bg="white" borderRadius="lg" boxShadow="sm" p={5}>
          <Flex alignItems="center" gap={3} mb={2}>
            <Flex
              w="40px"
              h="40px"
              borderRadius="md"
              bg="green.50"
              color="green.500"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={FiCheckCircle} />
            </Flex>
            <Stat>
              <StatLabel color="gray.500" fontSize="sm">
                Total pagado
              </StatLabel>
              <StatNumber fontSize="2xl">{formatCOP(overview.monthPaidAmount)}</StatNumber>
              <StatHelpText fontSize="xs" color="gray.400" mb={0}>
                {overview.monthLabel}
              </StatHelpText>
            </Stat>
          </Flex>
        </Box>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        <Box bg="white" borderRadius="lg" boxShadow="sm" p={5}>
          <Heading size="sm" mb={4}>
            Pendiente por concepto
          </Heading>
          {overview.pendingByConcept?.length ? (
            <Box>
              {overview.pendingByConcept.map((row) => (
                <Flex
                  key={row.charge_type}
                  justifyContent="space-between"
                  alignItems="center"
                  py={3}
                  borderBottom="1px solid"
                  borderColor="gray.100"
                  _last={{ borderBottom: 'none' }}
                >
                  <Box>
                    <Text fontWeight="medium">
                      {CHARGE_TYPE_LABELS[row.charge_type] || row.charge_type}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {row.count} cargo{row.count === 1 ? '' : 's'}
                    </Text>
                  </Box>
                  <Text fontWeight="semibold">{formatCOP(row.amount)}</Text>
                </Flex>
              ))}
              <Flex justifyContent="space-between" alignItems="center" pt={3} mt={2} borderTop="2px solid" borderColor="gray.200">
                <Text fontWeight="bold">Total adeudado</Text>
                <Text fontWeight="bold">{formatCOP(overview.pendingAmount)}</Text>
              </Flex>
            </Box>
          ) : (
            <Text color="gray.500" fontSize="sm" py={4}>
              No tienes cargos pendientes.
            </Text>
          )}
        </Box>

        <Box bg="white" borderRadius="lg" boxShadow="sm" p={5}>
          <Heading size="sm" mb={4}>
            Último pago registrado
          </Heading>
          {overview.lastPayment ? (
            <Box>
              <Text fontSize="3xl" fontWeight="bold" color="green.500" mb={2}>
                {formatCOP(overview.lastPayment.amount)}
              </Text>
              <Text fontSize="sm" color="gray.500" mb={4}>
                {dayjs(overview.lastPayment.payment_date).format('DD/MM/YY')} ·{' '}
                {METHOD_LABELS[overview.lastPayment.method] || overview.lastPayment.method}
              </Text>
              <Flex justifyContent="space-between" py={2} borderTop="1px solid" borderColor="gray.100">
                <Text color="gray.500" fontSize="sm">
                  Referencia
                </Text>
                <Text fontWeight="medium" fontSize="sm">
                  {overview.lastPayment.bank_reference}
                </Text>
              </Flex>
              <Flex justifyContent="space-between" py={2} borderTop="1px solid" borderColor="gray.100">
                <Text color="gray.500" fontSize="sm">
                  Cargos liquidados
                </Text>
                <Text fontWeight="medium" fontSize="sm">
                  {overview.lastPayment.chargesCount}
                </Text>
              </Flex>
              <Button
                variant="link"
                colorScheme="purple"
                rightIcon={<FiArrowRight />}
                mt={4}
                onClick={onGoToPagos}
              >
                Ver historial completo
              </Button>
            </Box>
          ) : (
            <Text color="gray.500" fontSize="sm" py={4}>
              No hay pagos registrados aún.
            </Text>
          )}
        </Box>
      </SimpleGrid>
    </Box>
  );
}
